import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { T } from './theme';

const API = 'https://tcio-api.onrender.com';
const hdrs = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('tcio_token') } });
const fmtDT = (v) => v ? new Date(v).toLocaleString('en-US',
  { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const btn = (primary) => ({ borderRadius: 8, padding: '8px 14px', font: 'inherit', fontWeight: 600,
  fontSize: 13, cursor: 'pointer', background: primary ? T.rose : '#fff',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });
const inp = { font: 'inherit', fontSize: 14, padding: '9px 10px', borderRadius: 8,
  border: `1px solid ${T.b300}`, width: '100%', boxSizing: 'border-box' };

const VField = ({ k, label, ph, value, onChange }) => (
  <div>
    <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: T.inkSoft, fontWeight: 700, marginBottom: 3 }}>{label}</div>
    <input style={inp} inputMode="decimal" placeholder={ph} value={value} onChange={e => onChange(k, e.target.value)} />
  </div>
);

// Shared entry modal — used by the facesheet panel AND the rounds board.
export function VitalsModal({ episodeId, patientName, onClose, onSaved }) {
  const BLANK = { systolic: '', diastolic: '', pulse: '', resp_rate: '', temp_f: '', spo2: '', notes: '', glucose: '', glucose_timing: '' };
  const NC_OPTIONS = [
    { v: 'off_site_medical', label: 'Off-site medical care' },
    { v: 'gym', label: 'Gym' },
    { v: 'recovery_meeting', label: 'Recovery meeting' },
    { v: 'community_integration', label: 'Community integration group' },
    { v: 'other', label: 'Other' },
  ];
  const [mode, setMode] = useState('capture');   // 'capture' | 'not_captured'
  const [ncReason, setNcReason] = useState('');
  const [ncDetail, setNcDetail] = useState('');
  const [f, setF] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [warns, setWarns] = useState(null);   // hold-parameter warnings after save
  // Facility clinical parameters (Catherine e02f4c1d): banded readings after save, with witness stamps.
  const [alerts, setAlerts] = useState(null);
  const [emsText, setEmsText] = useState(null);
  const [heldWarns, setHeldWarns] = useState(null);
  const [stamps, setStamps] = useState({});
  const VLABEL = { systolic: 'Systolic BP', diastolic: 'Diastolic BP', pulse: 'Pulse', resp_rate: 'Resp rate', temp_f: 'Temperature', spo2: 'SpO2', glucose: 'Blood glucose' };
  const stamp = async (a, kind) => {
    try {
      await axios.post(`${API}/api/emr/clinical-alerts/${a.alert_id}/stamp`, { kind }, hdrs());
      setStamps(p => ({ ...p, [a.alert_id + kind]: true }));
    } catch (e) { setStamps(p => ({ ...p, [a.alert_id + kind]: true })); }   // 409 = already stamped; treat as done
  };
  const finishAlerts = () => { const hw = heldWarns; setAlerts(null); setHeldWarns(null); if (hw) setWarns(hw); else { onSaved && onSaved(); onClose(); } };
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setErr('');
    if (mode === 'not_captured') {
      if (!ncReason) { setErr('Select where the client is'); return; }
      if (!ncDetail.trim()) { setErr('A note stating where the client is / what they are doing is required'); return; }
      setSaving(true);
      try {
        await axios.post(`${API}/api/emr/vitals`,
          { episode_id: episodeId, not_captured: true, not_captured_reason: ncReason, not_captured_detail: ncDetail.trim() }, hdrs());
        onSaved && onSaved(); onClose();
      } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Save failed'); }
      finally { setSaving(false); }
      return;
    }
    setSaving(true);
    try {
      // Blood glucose is saved to its own record (never counts as vitals). It can be entered alone or with vitals.
      const { glucose, glucose_timing, ...vit } = f;
      const hasVit = ['systolic', 'diastolic', 'pulse', 'resp_rate', 'temp_f', 'spo2'].some(k => String(vit[k] || '').trim().length > 0);
      const hasGlu = String(glucose || '').trim().length > 0;
      if (hasVit === false && hasGlu === false) { setErr('Enter at least one reading'); setSaving(false); return; }
      let r = { data: {} };
      if (hasVit) r = await axios.post(`${API}/api/emr/vitals`, { episode_id: episodeId, ...vit }, hdrs());
      let ga = []; let gErr = null;
      if (hasGlu) {
        try { const g = await axios.post(`${API}/api/emr/glucose`, { episode_id: episodeId, value_mg_dl: String(glucose).trim(), timing: glucose_timing || null, notes: hasVit ? null : (vit.notes || null) }, hdrs()); ga = g.data.param_alerts || []; }
        catch (ge) { gErr = (ge.response && ge.response.data && ge.response.data.error) || 'Save failed'; }
      }
      if (gErr && hasVit === false) { setErr(gErr); setSaving(false); return; }
      if (gErr) window.alert('The blood glucose reading was NOT saved: ' + gErr + '\n\nThe other vitals were saved. Open Enter Vitals again and enter only the glucose.');
      const hw = r.data.hold_warnings || [];
      const pa = [...(r.data.param_alerts || []), ...ga];
      if (pa.length) { setAlerts(pa); setEmsText(r.data.ems_text || null); setHeldWarns(hw.length ? hw : null); }
      else if (hw.length) setWarns(hw);      // show the witness screen before closing
      else { onSaved && onSaved(); onClose(); }
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 20,
        width: '100%', maxWidth: 460, boxShadow: '0 24px 64px rgba(15,23,42,0.35)' }}>
        {alerts ? (() => {
          const worst = alerts.some(a => a.band === 'emergency') ? 'emergency' : 'notify';
          const C = worst === 'emergency' ? { c: '#dc2626', bg: '#fef2f2' } : { c: '#b45309', bg: '#fffbeb' };
          return (<>
            <div style={{ fontWeight: 900, fontSize: 16, color: C.c, marginBottom: 4 }}>{worst === 'emergency' ? '\u26A0 CALL 911 \u2014 Emergency parameters' : '\u26A0 Notify medical provider'}</div>
            <div style={{ fontSize: 13, marginBottom: 10 }}>Vitals were saved. These readings are outside facility parameters:</div>
            {alerts.map(a => {
              const col = a.band === 'emergency' ? { c: '#dc2626', bg: '#fef2f2' } : { c: '#b45309', bg: '#fffbeb' };
              return (
                <div key={a.alert_id} style={{ background: col.bg, border: '1px solid ' + col.c, borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 13 }}>
                  <div style={{ fontWeight: 800 }}>{(VLABEL[a.key] || a.key) + ' ' + a.value + ' \u2014 ' + a.label}</div>
                  <div style={{ marginTop: 4 }}>{a.response}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => stamp(a, 'provider_notified')} disabled={!!stamps[a.alert_id + 'provider_notified']}
                      style={{ ...btn(false), background: stamps[a.alert_id + 'provider_notified'] ? '#e2e8f0' : '#fff' }}>
                      {stamps[a.alert_id + 'provider_notified'] ? '\u2713 Provider notified' : 'Provider notified'}</button>
                    <button onClick={() => stamp(a, 'ems_activated')} disabled={!!stamps[a.alert_id + 'ems_activated']}
                      style={{ ...btn(false), color: '#dc2626', borderColor: '#dc2626', background: stamps[a.alert_id + 'ems_activated'] ? '#e2e8f0' : '#fff' }}>
                      {stamps[a.alert_id + 'ems_activated'] ? '\u2713 911 activated' : '911 activated'}</button>
                  </div>
                </div>
              );
            })}
            {emsText && <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 4 }}>{emsText}</div>}
            <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 6 }}>Repeat vitals within 5-15 minutes and assess with symptoms, COWS/CIWA score, medications and baseline.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={btn(true)} onClick={finishAlerts}>Done</button>
            </div>
          </>);
        })() : !warns ? (<>
          <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 2 }}>Enter Vitals</div>
          {patientName && <div style={{ fontSize: 13, color: T.inkSoft, marginBottom: 10 }}>{patientName}</div>}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <button onClick={() => setMode('capture')} style={{ ...btn(mode === 'capture'), flex: 1 }}>Capture Vitals</button>
            <button onClick={() => setMode('not_captured')} style={{ ...btn(mode === 'not_captured'), flex: 1 }}>Not Captured — Off Site</button>
          </div>
          {mode === 'not_captured' ? (<>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: T.inkSoft, fontWeight: 700, marginBottom: 3 }}>Where is the client?</div>
            <select style={{ ...inp, marginBottom: 10 }} value={ncReason} onChange={e => setNcReason(e.target.value)}>
              <option value="">Select...</option>
              {NC_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: T.inkSoft, fontWeight: 700, marginBottom: 3 }}>Required note — location / activity detail</div>
            <textarea style={{ ...inp, fontFamily: 'inherit' }} rows={3} value={ncDetail}
              placeholder="e.g. Client at outside cardiology appointment with staff transport, expected back 2 PM"
              onChange={e => setNcDetail(e.target.value)} />
            <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 8 }}>
              This records a documented exception. It satisfies the vitals schedule for this slot but does NOT count as a reading for medication hold checks.
            </div>
          </>) : (<>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <VField k="systolic" label="Systolic" ph="120" value={f.systolic} onChange={set} />
            <VField k="diastolic" label="Diastolic" ph="80" value={f.diastolic} onChange={set} />
            <VField k="pulse" label="Pulse" ph="72" value={f.pulse} onChange={set} />
            <VField k="resp_rate" label="Resp Rate" ph="16" value={f.resp_rate} onChange={set} />
            <VField k="temp_f" label="Temp °F" ph="98.6" value={f.temp_f} onChange={set} />
            <VField k="spo2" label="SpO2 %" ph="98" value={f.spo2} onChange={set} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.b100}` }}>
            <VField k="glucose" label="Glucose mg/dL" ph="110" value={f.glucose} onChange={set} />
            <div>
              <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: T.inkSoft, fontWeight: 700, marginBottom: 3 }}>When (optional)</div>
              <select style={inp} value={f.glucose_timing} onChange={e => set('glucose_timing', e.target.value)}>
                <option value="">Select...</option><option value="fasting">Fasting</option><option value="before_meal">Before a meal</option>
                <option value="after_meal">After a meal</option><option value="bedtime">Bedtime</option><option value="other">Other</option>
              </select>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: T.inkSoft, marginTop: 4 }}>Finger stick blood glucose. It can be saved by itself, without other vitals.</div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: T.inkSoft, fontWeight: 700, marginBottom: 3 }}>Notes (optional)</div>
            <textarea style={{ ...inp, fontFamily: 'inherit' }} rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          </>)}
          {err && <div style={{ color: T.blocking, background: T.blockingBg, padding: 8, borderRadius: 8, marginTop: 10, fontWeight: 700, fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <button style={btn(false)} onClick={onClose}>Cancel</button>
            <button style={btn(true)} disabled={saving} onClick={save}>{saving ? 'Saving…' : mode === 'not_captured' ? 'Save Exception' : 'Save Vitals'}</button>
          </div>
        </>) : (<>
          <div style={{ fontWeight: 900, fontSize: 16, color: '#b45309', marginBottom: 8 }}>⚠ Hold Parameters Triggered</div>
          <div style={{ fontSize: 13, marginBottom: 10 }}>Vitals were saved. This reading is below provider hold parameters — notify medical before the next dose:</div>
          {warns.map((w, i) => (
            <div key={i} style={{ background: '#fffbeb', border: '1px solid #b45309', borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 800 }}>{w.medication}</div>
              <div>{w.breaches.join(' · ')}</div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button style={btn(true)} onClick={() => { onSaved && onSaved(); onClose(); }}>Acknowledged</button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// Facesheet panel: next-due chip + Enter Vitals + history strip.
export default function VitalsPanel({ episodeId, patientName }) {
  const [next, setNext] = useState(null);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [openAlerts, setOpenAlerts] = useState([]);   // clinical_alerts not yet acknowledged
  const [gluRows, setGluRows] = useState([]);         // blood glucose readings (their own record)
  const [lastScore, setLastScore] = useState(null);   // latest withdrawal score + reassessment clock

  const load = useCallback(async () => {
    if (!episodeId) return;
    try {
      const [n, h, a, w] = await Promise.all([
        axios.get(`${API}/api/emr/vitals/next?episodeId=${episodeId}`, hdrs()).catch(() => null),
        axios.get(`${API}/api/emr/vitals?episodeId=${episodeId}`, hdrs()).catch(() => null),
        axios.get(`${API}/api/emr/clinical-alerts?episodeId=${episodeId}`, hdrs()).catch(() => null),
        axios.get(`${API}/api/emr/withdrawal-scores?episodeId=${episodeId}`, hdrs()).catch(() => null),
      ]);
      if (n) setNext(n.data);
      if (h) setRows(h.data.data || []);
      if (a) setOpenAlerts((a.data.alerts || []).filter(x => !x.acknowledged_at));
      if (w) setLastScore((w.data.scores || [])[0] || null);
      const g = await axios.get(`${API}/api/emr/glucose?episodeId=${episodeId}`, hdrs()).catch(() => null); if (g) setGluRows(g.data.data || []);
    } catch (e) { /* silent */ }
  }, [episodeId]);
  const ackAlert = async (al) => {
    if (!window.confirm('Mark this alert acknowledged? Use once the medical provider has responded.')) return;
    try { await axios.post(`${API}/api/emr/clinical-alerts/${al.id}/stamp`, { kind: 'acknowledged' }, hdrs()); } catch (e) { /* 409 = already */ }
    load();
  };
  const VLABEL = { systolic: 'SBP', diastolic: 'DBP', pulse: 'Pulse', resp_rate: 'RR', temp_f: 'Temp', spo2: 'SpO2', glucose: 'Glucose', cows: 'COWS', ciwa_ar: 'CIWA' };
  useEffect(() => { load(); }, [load]);

  if (!episodeId) return null;
  const due = next && (next.overdue_minutes != null
    ? { txt: 'VITALS OVERDUE +' + next.overdue_minutes + 'm', c: '#dc2626', bg: '#fef2f2' }
    : next.due_now
      ? { txt: 'VITALS DUE NOW', c: '#dc2626', bg: '#fef2f2' }
      : { txt: 'Next vitals: ' + fmtDT(next.next_due), c: T.pass, bg: T.passBg });

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.b200}`, borderRadius: 10, padding: 14, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <strong>Vitals</strong>
        {due && <span style={{ background: due.bg, color: due.c, borderRadius: 99, padding: '2px 10px', fontSize: 11.5, fontWeight: 800 }}>{due.txt}</span>}
        {next && <span style={{ fontSize: 11, color: T.inkSoft }}>q{next.interval_minutes >= 1440 ? (next.interval_minutes / 1440) + 'd' : (next.interval_minutes / 60) + 'h'}</span>}
        {openAlerts.slice(0, 3).map(al => {
          const red = al.band === 'emergency' || al.band === 'urgent';
          const st = al.ems_activated_at ? '911 activated' : al.provider_notified_at ? 'provider notified' : 'unacknowledged';
          return (
            <span key={al.id} onClick={() => ackAlert(al)} title={(al.response_text || '') + ' - click to acknowledge'}
              style={{ background: red ? '#fef2f2' : '#fffbeb', color: red ? '#dc2626' : '#b45309', border: '1px solid ' + (red ? '#dc2626' : '#b45309'),
                borderRadius: 99, padding: '2px 10px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer' }}>
              {'\u26A0 ' + (VLABEL[al.key] || al.key) + ' ' + (al.value != null ? al.value : '') + ' \u00B7 ' + st}
            </span>
          );
        })}
        {lastScore && lastScore.reassess_due_at && (() => {
          const late = new Date(lastScore.reassess_due_at) < new Date();
          const col = late ? { c: '#dc2626', bg: '#fef2f2' } : lastScore.band === 'watch' ? { c: '#2563eb', bg: '#eff6ff' } : { c: '#b45309', bg: '#fffbeb' };
          return <span style={{ background: col.bg, color: col.c, borderRadius: 99, padding: '2px 10px', fontSize: 11.5, fontWeight: 800 }}>
            {(VLABEL[lastScore.scale] || lastScore.scale) + ' ' + lastScore.score + (lastScore.label ? ' (' + lastScore.label + ')' : '') + (late ? ' \u00B7 REASSESS OVERDUE' : ' \u00B7 reassess by ' + fmtDT(lastScore.reassess_due_at))}
          </span>;
        })()}
        <span style={{ flex: 1 }} />
        <button style={btn(true)} onClick={() => setOpen(true)}>+ Enter Vitals</button>
      </div>
      {rows.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr style={{ textAlign: 'left', color: T.inkSoft, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1 }}>
            <th style={{ padding: 4 }}>Taken</th><th>BP</th><th>Pulse</th><th>Resp</th><th>Temp</th><th>SpO2</th><th>By</th><th></th>
          </tr></thead>
          <tbody>
            {rows.slice(0, 8).map(v => (
              v.not_captured_reason ? (
              <tr key={v.id} style={{ borderTop: `1px solid ${T.b100}` }}>
                <td style={{ padding: 4 }}>{fmtDT(v.taken_at)}</td>
                <td colSpan={5}>
                  <span style={{ background: '#fff6e5', color: '#b45309', borderRadius: 6, padding: '2px 8px', fontSize: 11.5, fontWeight: 800 }}>
                    NOT CAPTURED — OFF SITE
                  </span>
                  <span style={{ fontSize: 12, color: T.inkSoft, marginLeft: 8 }}>{v.not_captured_detail}</span>
                </td>
                <td style={{ fontSize: 11, color: T.inkSoft }}>{v.taken_by_name}</td>
                <td></td>
              </tr>
              ) : (
              <tr key={v.id} style={{ borderTop: `1px solid ${T.b100}` }}>
                <td style={{ padding: 4 }}>{fmtDT(v.taken_at)}</td>
                <td style={{ fontWeight: 700 }}>{v.systolic != null && v.diastolic != null ? v.systolic + '/' + v.diastolic : '—'}</td>
                <td>{v.pulse != null ? v.pulse : '—'}</td>
                <td>{v.resp_rate != null ? v.resp_rate : '—'}</td>
                <td>{v.temp_f != null ? v.temp_f + '°' : '—'}</td>
                <td>{v.spo2 != null ? v.spo2 + '%' : '—'}</td>
                <td style={{ fontSize: 11, color: T.inkSoft }}>{v.taken_by_name}</td>
                <td>{v.hold_flags && <span title={JSON.stringify(v.hold_flags)} style={{ color: '#b45309', fontWeight: 800 }}>⚠</span>}</td>
              </tr>
              )
            ))}
          </tbody>
        </table>
      ) : <div style={{ color: T.inkSoft, fontSize: 13 }}>No vitals recorded this episode.</div>}
      {gluRows.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Blood glucose</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr style={{ textAlign: 'left', color: T.inkSoft, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1 }}>
              <th style={{ padding: 4 }}>Taken</th><th>mg/dL</th><th>When</th><th>By</th><th>Note</th>
            </tr></thead>
            <tbody>
              {gluRows.slice(0, 12).map(g => { const out = Array.isArray(g.param_flags) && g.param_flags.length > 0; return (
                <tr key={g.id} style={{ borderTop: `1px solid ${T.b100}` }}>
                  <td style={{ padding: 4 }}>{fmtDT(g.taken_at)}</td>
                  <td style={{ fontWeight: 800, color: out ? '#b45309' : undefined }}>{g.value_mg_dl}{out ? ' \u26A0' : ''}</td>
                  <td>{({ fasting: 'Fasting', before_meal: 'Before a meal', after_meal: 'After a meal', bedtime: 'Bedtime', other: 'Other' })[g.timing] || '\u2014'}</td>
                  <td style={{ fontSize: 11, color: T.inkSoft }}>{g.taken_by_name}</td>
                  <td style={{ fontSize: 12, color: T.inkSoft }}>{g.notes || ''}</td>
                </tr>); })}
            </tbody>
          </table>
        </div>
      )}
      {open && <VitalsModal episodeId={episodeId} patientName={patientName} onClose={() => setOpen(false)} onSaved={load} />}
    </div>
  );
}
